import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import * as adeProfilesApi from '@/services/adeProfilesApi';
import type { AdeProfileResponseDTO, AdeProfileDefineRequestDTO } from '@/types/api';

export interface AdeProfile {
  id: string;
  profile_name: string;
  description: string | null;
  profile_uuid: string | null;
  customer_id: string | null;
  abm_connection_id: string;
  device_count: number | null;
  is_mandatory: boolean | null;
  is_mdm_removable: boolean | null;
  await_device_configured: boolean | null;
  auto_advance_setup: boolean | null;
  skip_setup_items: any | null;
  configuration_web_url: string | null;
  support_phone_number: string | null;
  support_email_address: string | null;
  department: string | null;
  anchor_certs: string[] | null;
  supervising_host_certs: string[] | null;
  org_magic: string | null;
  is_draft: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  abm_connection?: {
    name: string;
    customer_id: string | null;
    organization_id: string;
  };
}

/** Map API response to local AdeProfile shape */
function mapToAdeProfile(dto: AdeProfileResponseDTO, adeAccountId: string): AdeProfile {
  return {
    id: dto.profileId,
    profile_name: dto.profileName,
    description: dto.description ?? null,
    profile_uuid: dto.profileId,
    customer_id: null,
    abm_connection_id: adeAccountId,
    device_count: dto.deviceCount ?? null,
    is_mandatory: (dto.configuration as any)?.is_mandatory ?? null,
    is_mdm_removable: (dto.configuration as any)?.is_mdm_removable ?? null,
    await_device_configured: (dto.configuration as any)?.await_device_configured ?? null,
    auto_advance_setup: (dto.configuration as any)?.auto_advance_setup ?? null,
    skip_setup_items: (dto.configuration as any)?.skip_setup_items ?? null,
    configuration_web_url: (dto.configuration as any)?.configuration_web_url ?? null,
    support_phone_number: (dto.configuration as any)?.support_phone_number ?? null,
    support_email_address: (dto.configuration as any)?.support_email_address ?? null,
    department: (dto.configuration as any)?.department ?? null,
    anchor_certs: null,
    supervising_host_certs: null,
    org_magic: null,
    is_draft: false,
    created_at: dto.createdAt ?? null,
    updated_at: dto.updatedAt ?? null,
  };
}

export const useAdeProfiles = (adeAccountId?: string) => {
  const [profiles, setProfiles] = useState<AdeProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (adeAccountId) {
      fetchProfiles();
    } else {
      setLoading(false);
    }
  }, [adeAccountId]);

  const fetchProfiles = async () => {
    if (!adeAccountId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await adeProfilesApi.listAdeProfiles(adeAccountId);
      setProfiles(data.map((d) => mapToAdeProfile(d, adeAccountId)));
    } catch (err) {
      console.error('Error fetching ADE profiles:', err);
      setError(err instanceof Error ? err : new Error('Failed to fetch enrollment profiles'));
      toast({ title: 'Error', description: 'Failed to load enrollment profiles', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const createProfile = async (profileData: {
    customer_id: string;
    profile_name: string;
    description?: string;
    abm_connection_id: string;
    is_mandatory?: boolean;
    is_mdm_removable?: boolean;
    await_device_configured?: boolean;
    auto_advance_setup?: boolean;
    skip_setup_items?: any;
    configuration_web_url?: string;
    support_phone_number?: string;
    support_email_address?: string;
    department?: string;
    is_draft?: boolean;
  }) => {
    const accountId = profileData.abm_connection_id || adeAccountId;
    if (!accountId) throw new Error('No ADE account ID');

    try {
      const body: AdeProfileDefineRequestDTO = {
        profileName: profileData.profile_name,
        description: profileData.description,
        configuration: {
          is_mandatory: profileData.is_mandatory,
          is_mdm_removable: profileData.is_mdm_removable,
          await_device_configured: profileData.await_device_configured,
          auto_advance_setup: profileData.auto_advance_setup,
          skip_setup_items: profileData.skip_setup_items,
          configuration_web_url: profileData.configuration_web_url,
          support_phone_number: profileData.support_phone_number,
          support_email_address: profileData.support_email_address,
          department: profileData.department,
        },
      };
      const result = await adeProfilesApi.createAdeProfile(accountId, body);
      toast({ title: 'Success', description: 'Enrollment profile created successfully' });
      await fetchProfiles();
      return result;
    } catch (err) {
      console.error('Error creating ADE profile:', err);
      toast({ title: 'Error', description: 'Failed to create enrollment profile', variant: 'destructive' });
      throw err;
    }
  };

  const updateProfile = async (id: string, updates: Partial<AdeProfile>) => {
    // The API doesn't expose an update endpoint; for now, log a warning
    console.warn('updateProfile not supported by the API yet', id, updates);
    toast({ title: 'Info', description: 'Profile update is not supported yet' });
  };

  const deleteProfile = async (id: string) => {
    if (!adeAccountId) return;
    try {
      await adeProfilesApi.deleteAdeProfile(adeAccountId, id);
      toast({ title: 'Success', description: 'Enrollment profile deleted successfully' });
      await fetchProfiles();
    } catch (err) {
      console.error('Error deleting ADE profile:', err);
      toast({ title: 'Error', description: 'Failed to delete enrollment profile', variant: 'destructive' });
      throw err;
    }
  };

  return { profiles, loading, error, fetchProfiles, createProfile, updateProfile, deleteProfile };
};
